import { useNavigate, useLocation } from "react-router-dom";
import { Flame, LogOut, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/data/i18n";
import { LANGUAGES } from "@/data/i18n";
import { API } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function Navbar() {
  const { user, subscription, lang, setLang, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const navItem = (path, label, testid) => (
    <button onClick={() => navigate(path)} data-testid={testid}
            className={`text-sm font-medium transition-colors ${location.pathname === path ? "text-red-500" : "text-zinc-400 hover:text-white"}`}>
      {label}
    </button>
  );

  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <header className="no-print sticky top-0 z-50 border-b border-zinc-800/80 glass">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="navbar-brand">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-500 red-glow">
            <Flame size={20} className="text-white" />
          </div>
          <div className="text-left leading-none">
            <div className="font-display text-xl font-extrabold uppercase tracking-wider text-white">VOLT</div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500">{tr(lang, "brand_tag")}</div>
          </div>
        </button>

        <nav className="hidden items-center gap-6 md:flex">
          {navItem("/", tr(lang, "nav_dashboard"), "nav-dashboard")}
          {user && navItem("/saved", tr(lang, "nav_saved"), "nav-saved")}
          {user && navItem("/calendar", tr(lang, "nav_calendar"), "nav-calendar")}
          {user && navItem("/diary", tr(lang, "nav_diary"), "nav-diary")}
          {navItem("/pricing", tr(lang, "nav_pricing"), "nav-pricing")}
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 rounded-full border border-zinc-800 px-3 py-1.5 text-sm text-zinc-300 hover:border-red-500/40" data-testid="lang-selector">
                <Globe size={15} /> {current.flag}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-zinc-800 bg-zinc-900">
              {LANGUAGES.map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)}
                                  className="cursor-pointer text-zinc-200 focus:bg-zinc-800 focus:text-white" data-testid={`lang-${l.code}`}>
                  <span className="mr-2">{l.flag}</span>{l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2" data-testid="user-profile-menu">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-9 w-9 rounded-full border border-zinc-700 object-cover" />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-500 text-sm font-bold text-white">
                      {user.name?.[0]}
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 border-zinc-800 bg-zinc-900">
                <div className="px-2 py-2">
                  <div className="text-sm font-semibold text-white">{user.name}</div>
                  <div className="truncate text-xs text-zinc-500">{user.email}</div>
                  {subscription && (
                    <div className="mt-2 rounded-lg bg-zinc-800 px-2 py-1 text-xs">
                      {subscription.subscribed ? (
                        <span className="text-emerald-400">✓ {subscription.plan_name}</span>
                      ) : subscription.trial_active ? (
                        <span className="text-orange-400">{subscription.trial_days_left} {tr(lang, "trial_days_left")}</span>
                      ) : (
                        <span className="text-red-400">{tr(lang, "trial_ended")}</span>
                      )}
                    </div>
                  )}
                </div>
                <DropdownMenuItem onClick={() => navigate("/saved")} className="cursor-pointer text-zinc-200 focus:bg-zinc-800 focus:text-white">
                  {tr(lang, "nav_saved")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400 focus:bg-zinc-800 focus:text-red-300" data-testid="logout-btn">
                  <LogOut size={15} className="mr-2" /> {tr(lang, "logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button onClick={login} data-testid="google-login-button"
                    className="rounded-full bg-red-500 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-red-600 red-glow">
              {tr(lang, "login")}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
